/**
 * Landing (logged-out) — hero carousel, live wins ticker, featured game
 * grid, why-bra-game strip, promos, providers, footer.
 */
import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Shield, Zap, MessageCircle, Crown, ChevronRight } from "lucide-react";

import AppShell from "../components/ui-r1/AppShell";
import HeroCarousel from "../components/ui-r1/HeroCarousel";
import LiveActivityTicker from "../components/ui-r1/LiveActivityTicker";
import { FeaturedGameTile, ComingSoonTile } from "../components/ui-r1/GameTile";
import PromoCard from "../components/ui-r1/PromoCard";
import Button from "../components/ui-r1/Button";
import { Badge } from "../components/ui-r1/Badge";
import BonusPromoStrip from "../components/BonusPromoStrip";
import { asset } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navigate } from "react-router-dom";

const HEROS = [
  { image: "/assets/brand/hero_1_welcome.png",  title: "Welcome bonus — 100% up to $500",             subtitle: "Match your first qualifying deposit. New players only. No wagering.", cta: "Claim bonus", href: "/signup" },
  { image: "/assets/brand/hero_2_bra_3way.png", title: "Bra 3-Way — Reveal the color, win 2.85×",     subtitle: "Provably fair. Three colors. One dress. Play in demo or real mode.",     cta: "Play now",    href: "/game/bra-3-way" },
  { image: "/assets/brand/hero_3_vip.png",      title: "VIP — cashback, fast payouts, personal host", subtitle: "Deposit more than $500 lifetime and unlock exclusive perks.",             cta: "See VIP",     href: "/vip" },
];

const COMING = [
  { id: "roulette",   title: "Bra Roulette",    image: "/assets/brand/coming_bra_roulette.png" },
  { id: "slots",      title: "Bra Slots",       image: "/assets/brand/coming_bra_slots.png" },
  { id: "blackjack",  title: "Bra Blackjack",   image: "/assets/brand/coming_bra_blackjack.png" },
];

// Sexy Tattoo's featured tile uses the first generated model image as its hero
// art (falls back to a static gradient if the asset hasn't been generated yet).
const SEXY_TATTOO_HERO = "/api/assets/models/sexy-tattoo/model_01/covered.png";
// Hot Bikini's featured tile — same pattern, `model_01/dressed.png` is the
// pre-reveal editorial dress shot (before the swap to red / black bikini).
const HOT_BIKINI_HERO = "/api/assets/models/hot-bikini/model_01/dressed.png";
// Scratch Card's featured tile — the dressed shot doubles as the tile hero so
// players see the model they'll be scratching to reveal.
const SCRATCH_CARD_HERO = "/api/assets/models/scratch-card/model_01/dressed.png";

const PROVIDERS = ["PragmaticBra","EvoBra","SoftBra","MicroBra","BraGaming","PlayBra"];

export default function Landing() {
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const slides = useMemo(() => HEROS.map((h) => ({ ...h, onCta: () => nav(h.href) })), [nav]);
  const featuredImage3Way = "/assets/brand/hero_2_bra_3way.png";
  const featuredImage2Way = "/assets/brand/hero_2_bra_3way.png";  // Reuse hero art for now; per-game hero art can ship later.
  const playersOnline3Way = useMemo(() => 200 + Math.floor(Math.random() * 200), []);
  const playersOnline2Way = useMemo(() => 120 + Math.floor(Math.random() * 180), []);
  const playersOnlineTattoo = useMemo(() => 90 + Math.floor(Math.random() * 150), []);
  const playersOnlineBikini = useMemo(() => 100 + Math.floor(Math.random() * 160), []);
  const playersOnlineScratch = useMemo(() => 80 + Math.floor(Math.random() * 140), []);
  if (!loading && user) return <Navigate to="/dashboard" replace />;

  return (
    <AppShell activeSection="casino" pageTitle="Home">
      <LiveActivityTicker />

      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 py-6 space-y-10">
        {/* Hero */}
        <HeroCarousel slides={slides} />

        {/* Live promotions strip (top 3) — feeds from /api/promotions,
            renders nothing if the feed is empty. Sits between hero and
            featured games so unauthed visitors see rewards early. */}
        <BonusPromoStrip limit={3} unauthenticated testid="landing-promo-strip" />

        {/* Featured + coming soon */}
        <section id="games" data-testid="landing-featured">          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-pink mb-1">Featured</div>
              <h2 className="font-display italic text-2xl sm:text-3xl text-ink">Play now</h2>
            </div>
            <Link to="#soon" className="text-[10px] tracking-widest uppercase text-ink-2 hover:text-pink">More games soon <ChevronRight className="w-3 h-3 inline" /></Link>
          </div>
          {/* 5 featured tiles: 1/row on mobile, 2/row on md, 5/row on lg. The
              5th tile (Scratch Card) is the newest drop. Coming-soon strip
              lives in its own row below so future launches can extend the
              featured grid without breaking the coming-soon flow. */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <FeaturedGameTile
              image={featuredImage3Way}
              title="Bra 3-Way"
              subtitle="Provably-fair color reveal · 2.85× payout"
              playersOnline={playersOnline3Way}
              onPlay={() => nav(user ? "/game/bra-3-way" : "/signup")}
              testid="landing-featured-3way-tile"
            />
            <FeaturedGameTile
              image={featuredImage2Way}
              title="Bra 2-Way"
              subtitle="Faster · simpler · 2 colors · 1.85× payout"
              playersOnline={playersOnline2Way}
              onPlay={() => nav(user ? "/game/bra-2-way" : "/signup")}
              testid="landing-featured-2way-tile"
            />
            <FeaturedGameTile
              image={SEXY_TATTOO_HERO}
              title="Sexy Tattoo"
              subtitle="Tattoo or no tattoo · 1.85× · new drop"
              playersOnline={playersOnlineTattoo}
              onPlay={() => nav(user ? "/game/sexy-tattoo" : "/signup")}
              testid="landing-featured-tattoo-tile"
              accent="pink"
            />
            <FeaturedGameTile
              image={HOT_BIKINI_HERO}
              title="Hot Bikini"
              subtitle="Red or black · 1.85× · fresh drop"
              playersOnline={playersOnlineBikini}
              onPlay={() => nav(user ? "/game/hot-bikini" : "/signup")}
              testid="landing-featured-bikini-tile"
              accent="pink"
            />
            <FeaturedGameTile
              image={SCRATCH_CARD_HERO}
              title="Scratch Card"
              subtitle="Buy · scratch · win up to 200× · fresh drop"
              playersOnline={playersOnlineScratch}
              onPlay={() => nav(user ? "/game/scratch-card" : "/signup")}
              testid="landing-featured-scratch-tile"
              accent="pink"
            />
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3" id="soon">
            {COMING.map((c) => <ComingSoonTile key={c.id} image={c.image} title={c.title} testid={`landing-coming-${c.id}`} />)}
          </div>
        </section>

        {/* Why */}
        <section data-testid="landing-why">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Shield, title: "Provably fair", body: "Every round auditable with HMAC-SHA256.", link: "/verify" },
              { icon: Zap, title: "Instant crypto", body: "BTC, USDT-TRC20, ETH — deposits credit on-chain confirm." },
              { icon: MessageCircle, title: "24/7 support", body: "Live in-app chat with a real human." },
              { icon: Crown, title: "VIP program", body: "Cashback, fast payouts, personal host.", link: "/vip" },
            ].map((f) => (
              <div key={f.title} className="rounded-xl border border-line bg-elevated p-5 hover:border-pink/60 transition group">
                <f.icon className="w-5 h-5 text-pink mb-3 group-hover:text-gold transition" />
                <div className="font-display italic text-lg text-ink mb-1">{f.title}</div>
                <div className="text-xs text-ink-2 leading-relaxed">{f.body}</div>
                {f.link && <Link to={f.link} className="text-[10px] tracking-widest uppercase text-pink mt-2 inline-flex items-center gap-1">Learn more <ChevronRight className="w-3 h-3" /></Link>}
              </div>
            ))}
          </div>
        </section>

        {/* Promotions strip */}
        <section data-testid="landing-promos">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-pink mb-1">Promotions</div>
              <h2 className="font-display italic text-2xl sm:text-3xl text-ink">Now on</h2>
            </div>
            <Link to="/promotions" className="text-[10px] tracking-widest uppercase text-ink-2 hover:text-pink">All promotions <ChevronRight className="w-3 h-3 inline" /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {HEROS.map((h, i) => (
              <PromoCard
                key={h.href || h.title}
                variant="tile"
                image={h.image}
                title={h.title.split("—")[0].trim()}
                subtitle={h.subtitle}
                cta={h.cta}
                onCta={() => nav(h.href)}
                testid={`landing-promo-${i}`}
              />
            ))}
          </div>
        </section>

        {/* Providers */}
        <section data-testid="landing-providers">
          <div className="text-[10px] tracking-[0.4em] uppercase text-ink-3 text-center mb-3">Powered by</div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {PROVIDERS.map((p) => (
              <div key={p} className="px-5 py-3 rounded-lg border border-line text-ink-3 hover:text-ink hover:border-ink-2 transition font-display italic text-lg opacity-60 hover:opacity-100">
                {p}
              </div>
            ))}
          </div>
        </section>

        {/* CTA banner (logged out only, so always shows) */}
        <section className="rounded-2xl border border-line overflow-hidden bg-gradient-to-r from-elevated to-highest p-8 sm:p-12 flex flex-col md:flex-row md:items-center gap-6" data-testid="landing-signup-cta">
          <div className="flex-1">
            <Badge variant="gold" className="mb-3">Welcome offer</Badge>
            <h3 className="font-display italic text-3xl sm:text-4xl text-ink mb-2">Sign up in 30 seconds.</h3>
            <p className="text-sm text-ink-2 max-w-lg">Instant demo credits, no card required. Add funds when you're ready to play for real.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/signup"><Button variant="primary" size="xl">Sign up</Button></Link>
            <Link to="/login"><Button variant="ghost" size="xl">Log in</Button></Link>
          </div>
        </section>
      </div>
    </AppShell>
  );
}
